import { Action, Reducer } from "redux";
import { AdventureAction, AdventureCharacterAdded, AdventureCharacterRemoved, AdventureCreated, AdventureGetEvents, AdventureLeave, AdventurePlayerJoined, AdventurePlayerLeft, AdventureReceiveNewEvent, AdventureSetCharacterChange, AdventureSetCommunalDice, AdventureSetCreating, AdventureSetEventsLoading, AdventureSetLeavingStatus, AdventureSetRolling, AdventuresLoaded, AdventureUpdateContentSharing, AdventureUpdateInvite, AdventureUpdateName, AdventureUpdatePrivateNotes, AdventureUpdatePublicNotes } from "./AdventureStore.Actions";
import { AdventureDefaultState, AdventureState } from "./AdventureStore.State";
import { Adventure, LocalAdventure } from "../../../entities/adventures/Adventure";
import { AdventureReducerHelpers } from "./AdventureReducerHelpers";

export const AdventureReducer: Reducer<AdventureState> = (state: AdventureState | undefined, incomingAction: Action): AdventureState => {
    if (state == undefined) {
        return AdventureDefaultState;
    }
    const action = incomingAction as AdventureAction;
    switch (action.type) {
        case 'ADVENTURES_LOADED': return loadAdventures(state, action);
        case 'ADVENTURES_LOAD_ERROR': return { ...state, error: true };

        case 'ADVENTURE_CREATED': return createAdventure(state, action);
        case 'ADVENTURE_LEAVE': return leaveAdventure(state, action);

        case 'ADVENTURE_SET_LEAVING_STATUS': return adventureSetLeavingStatus(state, action);
        case 'ADVENTURE_SET_CREATING': return setAdventureCreating(state, action);
        case 'ADVENTURE_UPDATE_NAME': return updateName(state, action);
        case 'ADVENTURE_UPDATE_CONTENT_SHARING': return updateContentSharing(state, action);
        case 'ADVENTURE_UPDATE_INVITE': return updateInvite(state, action);
        case 'ADVENTURE_UPDATE_PUBLIC_NOTES': return updatePublicnotes(state, action);
        case 'ADVENTURE_UPDATE_PRIVATE_NOTES': return updatePrivateNotes(state, action);

        case 'ADVENTURE_GET_EVENTS': return getAdventureEvents(state, action);
        case 'ADVENTURE_SET_EVENTS_LOADING': return setAdventureEventsLoading(state, action);
        case 'ADVENTURE_RECEIVE_NEW_EVENT': return receiveNewEvent(state, action);

        case 'ADVENTURE_SET_COMMUNAL_DICE': return updateCommunalDice(state, action);
        case 'ADVENTURE_SET_ROLLING': return updateSetRolling(state, action);

        case 'ADVENTURE_PLAYER_JOINED': return playerJoined(state, action);
        case 'ADVENTURE_PLAYER_LEFT': return playerLeft(state, action);
        case 'ADVENTURE_CHARACTER_ADDED': return addCharacter(state, action);
        case 'ADVENTURE_CHARACTER_REMOVED': return removeCharacter(state, action);

        case 'ADVENTURE_SET_CHARACTER_CHANGE': return setCharacterChange(state, action);
        default: return state;
    }
}

const createNewLocalAdventure = (adventure: Adventure): LocalAdventure => ({ ...adventure, rolling: false });

const loadAdventures = (state: AdventureState, action: AdventuresLoaded): AdventureState => {
    const { adventures } = action;

    const unchangedAdventures = state.adventures.filter(stateAdventure => !adventures.any(newAdventure => newAdventure.id == stateAdventure.id));
    return {
        ...state,
        loaded: true,
        error: false,
        adventures: [...unchangedAdventures, ...adventures.map(createNewLocalAdventure)]
    }
}

const createAdventure = (state: AdventureState, action: AdventureCreated): AdventureState => {
    const { adventure } = action;
    return { ...state, adventures: [...state.adventures, createNewLocalAdventure(adventure)] };
}

const leaveAdventure = (state: AdventureState, action: AdventureLeave): AdventureState => {
    const { id } = action;
    return {
        ...state,
        adventures: [...state.adventures.filter(a => a.id != id)]
    };
}

const adventureSetLeavingStatus = (state: AdventureState, action: AdventureSetLeavingStatus): AdventureState => {
    const { id, leaving } = action;
    if (leaving) {
        return { ...state, leaving: [...state.leaving.filter(aid => aid != id)] }
    }
    return {
        ...state,
        leaving: [...state.leaving, id]
    };
}

const setAdventureCreating = (state: AdventureState, action: AdventureSetCreating): AdventureState => {
    const { creating } = action;
    return { ...state, creating };
}

const updateName = (state: AdventureState, action: AdventureUpdateName): AdventureState => {
    const { id, name } = action;
    const adventure = AdventureReducerHelpers.getAdventure(state, id);
    if (adventure == null) {
        return state;
    }
    
    const newAdventure = {
        ...adventure,
        name
    }
    return AdventureReducerHelpers.updateAdventure(state, newAdventure);
}

const updateContentSharing = (state: AdventureState, action: AdventureUpdateContentSharing): AdventureState => {
    const { id, allowContentSharing } = action;
    const adventure = AdventureReducerHelpers.getAdventure(state, id);
    if (adventure == null) {
        return state;
    }

    const newAdventure = {
        ...adventure,
        allowContentSharing
    }
    return AdventureReducerHelpers.updateAdventure(state, newAdventure);
}

const updateInvite = (state: AdventureState, action: AdventureUpdateInvite): AdventureState => {
    const { id, inviteId } = action;
    const adventure = AdventureReducerHelpers.getAdventure(state, id);
    if (adventure == null) {
        return state;
    }

    const newAdventure = {
        ...adventure,
        inviteId
    }
    return AdventureReducerHelpers.updateAdventure(state, newAdventure);
}

const updatePublicnotes = (state: AdventureState, action: AdventureUpdatePublicNotes): AdventureState => {
    const { id, publicNotes } = action;
    const adventure = AdventureReducerHelpers.getAdventure(state, id);
    if (adventure == null) {
        return state;
    }

    const newAdventure = {
        ...adventure,
        publicNotes
    };
    return AdventureReducerHelpers.updateAdventure(state, newAdventure);
}

const updatePrivateNotes = (state: AdventureState, action: AdventureUpdatePrivateNotes): AdventureState => {
    const { id, privateNotes } = action;
    const adventure = AdventureReducerHelpers.getAdventure(state, id);
    if (adventure == null) {
        return state;
    }

    const newAdventure = {
        ...adventure,
        privateNotes
    }
    return AdventureReducerHelpers.updateAdventure(state, newAdventure);
}

const updateCommunalDice = (state: AdventureState, action: AdventureSetCommunalDice): AdventureState => {
    const { id, communalDice } = action;
    
    const adventure = AdventureReducerHelpers.getAdventure(state, id);
    if (adventure == null) {
        return state;
    }

    const newAdventure = {
        ...adventure,
        communalDice
    }
    return AdventureReducerHelpers.updateAdventure(state, newAdventure);
}

const updateSetRolling = (state: AdventureState, action: AdventureSetRolling): AdventureState => {
    const { id, rolling } = action;
    
    const adventure = AdventureReducerHelpers.getAdventure(state, id);
    if (adventure == null) {
        return state;
    }

    const newAdventure = {
        ...adventure,
        rolling
    }
    return AdventureReducerHelpers.updateAdventure(state, newAdventure);
}

const playerJoined = (state: AdventureState, action: AdventurePlayerJoined): AdventureState => {
    const { id, playerId } = action;
    const adventure = AdventureReducerHelpers.getAdventure(state, id);
    if (adventure == null) {
        return state;
    }

    const newAdventure = {
        ...adventure,
        playerIds: [...adventure.playerIds, playerId]
    }

    return AdventureReducerHelpers.updateAdventure(state, newAdventure);
}

const playerLeft = (state: AdventureState, action: AdventurePlayerLeft): AdventureState => {
    const { id, playerId } = action;
    const adventure = AdventureReducerHelpers.getAdventure(state, id);
    if (adventure == null) {
        return state;
    }

    const newAdventure = {
        ...adventure,
        playerIds: adventure.playerIds.filter(pid => pid != playerId)
    }

    return AdventureReducerHelpers.updateAdventure(state, newAdventure);
}

const addCharacter = (state: AdventureState, action: AdventureCharacterAdded): AdventureState => {
    const { id, characterId } = action;
    const adventure = AdventureReducerHelpers.getAdventure(state, id);
    if (adventure == null) {
        return state;
    }

    const newAdventure = { ...adventure, characterIds: [...adventure.characterIds, characterId] };
    return AdventureReducerHelpers.updateAdventure(state, newAdventure);
}

const removeCharacter = (state: AdventureState, action: AdventureCharacterRemoved): AdventureState => {
    const { id, characterId } = action;
    
    const adventure = AdventureReducerHelpers.getAdventure(state, id);
    if (adventure == null) {
        return state;
    }

    const newAdventure = {
        ...adventure,
        characterIds: adventure.characterIds.filter(cid => cid != characterId)
    }
    return AdventureReducerHelpers.updateAdventure(state, newAdventure);
}


/**
 * Events
 * */

const getAdventureEvents = (state: AdventureState, action: AdventureGetEvents): AdventureState => {
    const { id, events, done } = action;
    const adventureEvents = state.eventsByAdventureId[id] ?? [];
    return {
        ...state,
        eventsByAdventureId: {
            ...state.eventsByAdventureId,
            [id]: [...adventureEvents, ...events]
        },
        loadingEvents: {
            ...state.loadingEvents,
            [id]: { initialLoadPerformed: true, loading: false, done }
        }
    };
}

const setAdventureEventsLoading = (state: AdventureState, action: AdventureSetEventsLoading): AdventureState => {
    const { id, loading } = action;
    return {
        ...state,
        loadingEvents: {
            ...state.loadingEvents,
            [id]: { ...state.loadingEvents[id], loading }
        }
    };
}

const receiveNewEvent = (state: AdventureState, action: AdventureReceiveNewEvent): AdventureState => {
    const { id, event } = action;
    const adventureEvents = state.eventsByAdventureId[id] ?? [];
    return {
        ...state,
        eventsByAdventureId: {
            ...state.eventsByAdventureId,
            [id]: [event, ...adventureEvents]
        }
    };
}

const setCharacterChange = (state: AdventureState, action: AdventureSetCharacterChange): AdventureState => {
    const { modifyingCharacterOnAdventure } = action;
    return { ...state, modifyingCharacterOnAdventure };
}